import { useState } from 'react';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { useAdminSubmission, useAdminSubmissions, useReviewSubmission } from '../../hooks/useQuestAdmin';
import { fmtDate } from './questShared';
import {
  BTN, BTN_DANGER, BTN_SECONDARY, Card, ErrorNote, INPUT, Loading,
} from './adminUi';

// What employees send in for Comms to check (brief section 4, and "Manage
// submissions — approve, reject, request clarification"):
//   Customer compliments   verified -> the person complimented is paid
//   Improvement ideas      can be marked Reviewed first (the brief's
//                          Submitted -> Reviewed -> Approved), pays the sender
//   Colleague recognition  pays the colleague, never the sender
//   Challenge entries      a task Quest's note and photo; approving completes
//                          the Quest for the sender
// Rejecting or asking for detail needs a note: the sender sees it.

const STATUS_FILTERS = [
  ['pending', 'Waiting'],
  ['reviewed', 'Reviewed'],
  ['clarify', 'Asked for detail'],
  ['approved', 'Approved'],
  ['rejected', 'Not approved'],
  ['', 'All'],
];

const KIND_FILTERS = [
  ['', 'Everything'],
  ['compliment', 'Customer compliments'],
  ['improvement', 'Improvement ideas'],
  ['colleague', 'Colleague recognition'],
  ['task', 'Challenge entries'],
];

const KIND_TITLE = {
  compliment: 'Customer compliment',
  improvement: 'Improvement idea',
  colleague: 'Colleague recognition',
  task: 'Challenge entry',
};

// Each kind's answers, in order: [key, label, long text].
const FIELDS = {
  compliment: [['source', 'Where it came from'], ['reference', 'Job reference'], ['compliment', 'What the customer said', true]],
  improvement: [['idea', 'Idea'], ['department', 'Department'], ['problem', 'Problem it solves', true],
    ['improvement', 'Suggested improvement', true]],
  colleague: [['story', 'How they supported them', true]],
  task: [['note', 'What they did', true]],
};

// The photo or PDF, fetched only when asked for — a list of photos is megabytes.
function Attachment({ s }) {
  const [open, setOpen] = useState(false);
  const q = useAdminSubmission(s.id, open);
  const data = q.data?.attachment;
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <PaperClipIcon className="h-4 w-4" />
        {s.attachment_name || 'Show the attachment'}
      </button>
    );
  }
  if (q.isLoading) return <p className="mt-2 text-sm text-gray-400">Loading the attachment…</p>;
  if (!data) return <ErrorNote error={q.error} fallback="Could not load the attachment." />;
  return data.startsWith('data:application/pdf') ? (
    <a href={data} download={s.attachment_name || `submission-${s.id}.pdf`} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
      <PaperClipIcon className="h-4 w-4" />
      Download {s.attachment_name || 'the PDF'}
    </a>
  ) : (
    <a href={data} target="_blank" rel="noreferrer" className="mt-2 block">
      <img src={data} alt={s.attachment_name || 'Attachment'} className="max-h-80 rounded-md border border-gray-200" />
    </a>
  );
}

function Submission({ s }) {
  const review = useReviewSubmission();
  const [note, setNote] = useState('');
  const decide = (decision) => review.mutate({ id: s.id, decision, note });
  const open = ['pending', 'reviewed', 'clarify'].includes(s.status);
  const paysSomeoneElse = s.subject_entra_id !== s.submitter_entra_id;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-base font-semibold text-gray-900">{s.quest_title || KIND_TITLE[s.kind]}</span>
        <span className="text-sm text-gray-500">
          from {s.submitter_name || 'Unknown'}{paysSomeoneElse ? ` about ${s.subject_name || 'a colleague'}` : ''}
        </span>
        <span className="ml-auto text-xs text-gray-400">{fmtDate(s.created_at)}</span>
      </div>

      <dl className="mt-2 space-y-1">
        {(FIELDS[s.kind] || []).filter(([k]) => s.details[k]).map(([key, label, long]) => (
          <div key={key} className={long ? '' : 'flex gap-2'}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
            <dd className={`text-sm text-gray-800 ${long ? 'whitespace-pre-line' : ''}`}>{s.details[key]}</dd>
          </div>
        ))}
      </dl>
      {s.has_attachment ? <Attachment s={s} /> : null}

      {s.review_note ? (
        <p className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span className="font-semibold">{s.reviewed_by_name || 'Comms'}:</span> {s.review_note}
        </p>
      ) : null}
      {s.status === 'approved' ? (
        <p className="mt-2 text-sm text-emerald-700">
          Approved by {s.reviewed_by_name || 'Comms'} on {fmtDate(s.reviewed_at)} · +{s.points_awarded} points
          {paysSomeoneElse ? ` to ${s.subject_name || 'the colleague'}` : ''}
        </p>
      ) : null}
      {s.status === 'rejected' ? <p className="mt-2 text-sm text-gray-500">Not approved · {fmtDate(s.reviewed_at)}</p> : null}
      {s.status === 'reviewed' ? (
        <p className="mt-2 text-sm text-indigo-700">Reviewed by {s.reviewed_by_name || 'Comms'} on {fmtDate(s.reviewed_at)} — waiting for a decision.</p>
      ) : null}
      {s.status === 'clarify' ? (
        <p className="mt-2 text-sm text-sky-700">Waiting for {s.submitter_name || 'them'} to add detail.</p>
      ) : null}

      {open ? (
        <div className="mt-3 space-y-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} className={INPUT}
            placeholder="Note to the person who sent it (needed to ask for detail or to reject)" />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={review.isPending} onClick={() => decide('approve')} className={BTN}>
              {s.kind === 'compliment' ? 'Verified — approve' : 'Approve'}
            </button>
            {s.kind === 'improvement' && s.status === 'pending' ? (
              <button type="button" disabled={review.isPending} onClick={() => decide('review')} className={BTN_SECONDARY}>
                Mark reviewed
              </button>
            ) : null}
            <button type="button" disabled={review.isPending} onClick={() => decide('clarify')} className={BTN_SECONDARY}>
              Ask for detail
            </button>
            <button type="button" disabled={review.isPending} onClick={() => decide('reject')} className={BTN_DANGER}>
              Not approved
            </button>
          </div>
          {review.isError ? <ErrorNote error={review.error} /> : null}
        </div>
      ) : null}
    </Card>
  );
}

function SubmissionsTab() {
  const [status, setStatus] = useState('pending');
  const [kind, setKind] = useState('');
  const q = useAdminSubmissions(status, kind);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map(([k, label]) => (
            <button key={k || 'all'} type="button" onClick={() => setStatus(k)}
              className={`rounded-full px-3 py-1 text-sm font-semibold ${
                status === k ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={`${INPUT} ml-auto w-auto`}>
          {KIND_FILTERS.map(([k, label]) => <option key={k || 'all'} value={k}>{label}</option>)}
        </select>
      </div>
      <Loading q={q} what="the submissions" />
      {q.data && !q.data.length ? (
        <Card className="px-4 py-6 text-center text-sm text-gray-500">Nothing here.</Card>
      ) : null}
      {(q.data || []).map((s) => <Submission key={s.id} s={s} />)}
    </div>
  );
}

export default SubmissionsTab;
